import { getGetCaseTypesMockHandler } from "@vega-api/generated/case-admin-v1/case-admin-v1.msw";
import { getGetCaseAttentionMockHandler } from "@vega-api/generated/case-attention-v1/case-attention-v1.msw";
import { getGetCaseCommentsMockHandler } from "@vega-api/generated/case-comments-v1/case-comments-v1.msw";
import { getGetCaseEmailsMockHandler } from "@vega-api/generated/case-email-v1/case-email-v1.msw";
import {
  getGetCaseByIdMockHandler,
  getGetCaseByNumberMockHandler,
  getGetCasesMockHandler,
} from "@vega-api/generated/case-management-v1/case-management-v1.msw";
import { getGetFiltersByTableCodeMockHandler } from "@vega-api/generated/filters-v1/filters-v1.msw";
import { getGetFiltersByTableCodeResponseMock } from "@vega-api/generated/filters-v1/filters-v1-mocks.msw";
import { type ApiCaseAttentionResponse } from "@vega-api/generated/types/apiCaseAttentionResponse";
import { type ApiCaseCommentListResponse } from "@vega-api/generated/types/apiCaseCommentListResponse";
import { type ApiCaseEmailMessageListResponse } from "@vega-api/generated/types/apiCaseEmailMessageListResponse";
import { type ApiCaseListResponse } from "@vega-api/generated/types/apiCaseListResponse";
import { type ApiCaseResponse } from "@vega-api/generated/types/apiCaseResponse";
import { type ApiCaseTypeListResponse } from "@vega-api/generated/types/apiCaseTypeListResponse";
import { type ApiResponseProfileResponse } from "@vega-api/generated/types/apiResponseProfileResponse";
import { type ApiResponseSavedFilters } from "@vega-api/generated/types/apiResponseSavedFilters";
import { type ApiResponseUser } from "@vega-api/generated/types/apiResponseUser";
import {
  getGetProfileMockHandler,
  getGetUserByIdMockHandler,
} from "@vega-api/generated/user-management-v1/user-management-v1.msw";

/**
 * Custom mock handler for getting case by number that uses the actual caseNumber from the URL
 * instead of generating a random one. This ensures consistency between the case list
 * and the case detail page.
 */
export const getCustomCaseByNumberHandler = () =>
  getGetCaseByNumberMockHandler((info) => {
    // Extract caseNumber from the URL parameters
    const caseNumber = info.params.caseNumber as string;

    // Create a consistent mock response using the actual caseNumber from the URL
    const customCaseResponse: ApiCaseResponse = {
      data: {
        caseId: "case-id-123", // Generate a consistent caseId
        caseNumber,
        caseCategoryId: "case-category-1",
        caseCategoryName: "Investor Queries",
        caseTypeId: "case-type-2",
        caseTypeName: "Performance Issue",
        title: "Test Case - Dashboard Loading Issue",
        description:
          "The dashboard is taking too long to load when there are many funds displayed. This affects user experience significantly.",
        assigneeGroup: "ABC team",
        assignee: "user-1",
        status: "IN_PROGRESS",
        priority: "MEDIUM",
        emailAddress: "test@example.com",
        slaInHours: 24,
        slaBreachedTimestamp: "2024-12-31T08:00:00Z",
        createdAt: "2024-12-30T05:00:00Z",
        updatedAt: "2024-12-31T10:00:00Z",
      },
      meta: {},
      errors: {},
    };

    return customCaseResponse;
  });

/**
 * Custom mock handler for getting case by ID that uses the actual caseId from the URL
 * instead of generating a random one. This ensures consistency between the case list
 * and the case detail page.
 */
export const getCustomCaseByIdHandler = () =>
  getGetCaseByIdMockHandler((info) => {
    // Extract caseId from the URL parameters
    const caseId = info.params.caseId as string;

    // Create a consistent mock response using the actual caseId from the URL
    const customCaseResponse: ApiCaseResponse = {
      data: {
        caseId,
        caseNumber: `CS-${caseId.slice(-6).padStart(6, "0")}`,
        caseCategoryId: "case-category-1",
        caseCategoryName: "Investor Queries",
        caseTypeId: "case-type-2",
        caseTypeName: "Performance Issue",
        title: "Test Case - Dashboard Loading Issue",
        description:
          "The dashboard is taking too long to load when there are many funds displayed. This affects user experience significantly.",
        assigneeGroup: "ABC team",
        assignee: "user-1",
        status: "IN_PROGRESS",
        priority: "MEDIUM",
        emailAddress: "test@example.com",
        slaInHours: 24,
        slaBreachedTimestamp: "2024-12-31T08:00:00Z",
        createdAt: "2024-12-30T05:00:00Z",
        updatedAt: "2024-12-31T10:00:00Z",
      },
      meta: {},
      errors: {},
    };

    return customCaseResponse;
  });

/**
 * Custom mock handler for getting case types that returns subtypes matching our case mock data.
 * This ensures the subtype dropdown shows the correct data.
 */
export const getCustomCaseTypesHandler = () => {
  // Create a consistent mock response that matches our case mock data
  const customCaseTypesResponse: ApiCaseTypeListResponse = {
    data: {
      caseTypes: [
        {
          id: "case-type-1",
          categoryId: "category-1",
          name: "Bug Report",
          description: "Report a software bug",
          status: "ACTIVE",
          createdAt: "2024-03-20T10:00:00Z",
          updatedAt: "2024-03-20T10:00:00Z",
        },
        {
          id: "case-type-2",
          categoryId: "category-1",
          name: "Performance Issue",
          description: "Performance related problems",
          status: "ACTIVE",
          createdAt: "2024-03-20T10:00:00Z",
          updatedAt: "2024-03-20T10:00:00Z",
        },
      ],
    },
    meta: {},
    errors: {},
  };

  return getGetCaseTypesMockHandler(customCaseTypesResponse);
};

/**
 * Custom mock handler for the attention-signals endpoint that returns a
 * deterministic "unavailable" response, so f2f/vitest specs render the Cases
 * page consistently instead of the generated aggregator's random mock data.
 */
export const getCustomCaseAttentionHandler = () => {
  const customCaseAttentionResponse: ApiCaseAttentionResponse = {
    data: { available: false, cases: [] },
    meta: {},
    errors: {},
  };

  return getGetCaseAttentionMockHandler(customCaseAttentionResponse);
};

/**
 * Custom mock handler for getting user profile that returns a user with ID that can be used for assignment.
 * This ensures the assignee dropdown and "Assign to Me" functionality work correctly.
 */
export const getCustomProfileHandler = () => {
  // Create a consistent mock response with a user that can be assigned to cases
  const customProfileResponse: ApiResponseProfileResponse = {
    data: {
      id: "user-1", // Consistent user ID for assignment
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@vega.investments",
      phone: "+1234567890",
      partnerships: [
        {
          partnerId: "org-1",
          partnerType: "GENERAL",
        },
      ],
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z",
    },
    meta: {},
    errors: {},
  };

  return getGetProfileMockHandler(customProfileResponse);
};

export const getCustomCaseEmailsHandler = () =>
  getGetCaseEmailsMockHandler((info) => {
    const caseId = info.params.caseId as string;

    const customEmailsResponse: ApiCaseEmailMessageListResponse = {
      data: {
        emails: [
          {
            id: "email-1",
            caseId,
            direction: "INBOUND",
            fromEmail: "knox.pope@familyoffice.com",
            toEmails: ["support@vega.investments"],
            subject: "Dashboard loading issue",
            body: "Hi team,\n\nThe dashboard is taking too long to load when there are many funds displayed. Could you have a look?\n\nBest,\nKnox",
            participantName: "Knox Pope",
            status: "DELIVERED",
            createdAt: "2024-12-30T05:00:00Z",
          },
          {
            id: "email-2",
            caseId,
            direction: "OUTBOUND",
            fromEmail: "support@vega.investments",
            toEmails: ["knox.pope@familyoffice.com"],
            subject: "Re: Dashboard loading issue",
            body: "Hi Knox,\n\nThanks for flagging this — we are looking into the slow dashboard load and will follow up with an update shortly.\n\nKind regards,\nJohn",
            participantName: "John Doe",
            status: "SENT",
            createdAt: "2024-12-30T09:30:00Z",
          },
          {
            id: "email-3",
            caseId,
            direction: "INBOUND",
            fromEmail: "knox.pope@familyoffice.com",
            toEmails: ["support@vega.investments"],
            subject: "Re: Dashboard loading issue",
            body: "Thanks John, much appreciated. It seems worst on the funds page with the full portfolio expanded.",
            participantName: "Knox Pope",
            status: "DELIVERED",
            createdAt: "2024-12-31T08:15:00Z",
          },
        ],
      },
      meta: {},
      errors: {},
    };

    return customEmailsResponse;
  });

export const getCustomCaseCommentsHandler = () =>
  getGetCaseCommentsMockHandler((info) => {
    const caseId = info.params.caseId as string;

    const customCommentsResponse: ApiCaseCommentListResponse = {
      data: {
        comments: [
          {
            commentId: "comment-1",
            caseId,
            content:
              "Checked with the platform team — the slow load correlates with the valuations query. Waiting for their fix before replying.",
            createdBy: "user-1",
            createdAt: "2024-12-30T11:00:00Z",
          },
        ],
      },
      meta: { count: 1 },
      errors: {},
    };

    return customCommentsResponse;
  });

export const getCustomUserByIdHandler = () =>
  getGetUserByIdMockHandler((info) => {
    const userId = info.params.userId as string;

    const customUserResponse: ApiResponseUser = {
      data: {
        id: userId,
        firstName: "John",
        lastName: "Doe",
        email: "john.doe@example.com",
        ownerId: "org-1",
        ownerType: "GENERAL_PARTNER",
        status: "ACTIVE",
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
      meta: {},
      errors: {},
    };

    return customUserResponse;
  });

const MOCK_CASES = [
  {
    caseId: "case-id-123",
    caseNumber: "APO-001234",
    caseCategoryId: "123e4567-e89b-12d3-a456-426614174012",
    caseCategoryName: "Technical Issues",
    caseTypeId: "case-type-2",
    caseTypeName: "Performance Issue",
    title: "Request to change wire instructions",
    description:
      "Could you please check the wire instructions specific to subscription xyz to ABC.",
    status: "NEW",
    priority: "LOW",
    emailAddress: "user@example.com",
    slaInHours: 24,
    slaBreachedTimestamp: "2024-05-20T10:00:00Z",
    createdAt: "2024-03-20T10:00:00Z",
    updatedAt: "2024-03-20T10:30:00Z",
  },
  {
    caseId: "case-id-124",
    caseNumber: "APO-001235",
    caseCategoryId: "123e4567-e89b-12d3-a456-426614174012",
    caseCategoryName: "Technical Issues",
    caseTypeId: "case-type-2",
    caseTypeName: "Performance Issue",
    title: "Dashboard loading issue",
    description:
      "The dashboard is taking too long to load when there are many funds displayed.",
    assignee: "user-1",
    assigneeGroup: "ABC team",
    status: "IN_PROGRESS",
    priority: "MEDIUM",
    emailAddress: "knox.pope@familyoffice.com",
    slaInHours: 48,
    createdAt: "2024-03-19T08:00:00Z",
    updatedAt: "2024-03-21T09:00:00Z",
  },
  {
    caseId: "case-id-125",
    caseNumber: "APO-001236",
    caseCategoryId: "223e4567-e89b-12d3-a456-426614174012",
    caseCategoryName: "Investor Queries",
    caseTypeId: "case-type-1",
    caseTypeName: "Change Request",
    title: "Quarterly report request",
    description: "LP requests the Q4 report for Fund III.",
    status: "RESOLVED",
    priority: "HIGH",
    emailAddress: "meridian@pensiontrust.com",
    slaInHours: 24,
    createdAt: "2024-03-15T12:00:00Z",
    updatedAt: "2024-03-18T16:00:00Z",
  },
] satisfies NonNullable<ApiCaseListResponse["data"]>["cases"];

const readListParams = (url: URL, name: string) =>
  [...url.searchParams.getAll(name), ...url.searchParams.getAll(`${name}[]`)]
    .flatMap((value) => value.split(","))
    .filter(Boolean);

const filterMockCases = (url: URL) => {
  const statuses = readListParams(url, "status");
  const assignees = readListParams(url, "assignee");
  const priorities = readListParams(url, "priority");
  const searchQuery = url.searchParams.get("searchQuery")?.toLowerCase();
  const overdue = url.searchParams.get("overdue") === "true";

  let cases = MOCK_CASES;

  if (overdue) {
    cases = cases.filter((mockCase) => "slaBreachedTimestamp" in mockCase);
  }

  if (statuses.length > 0) {
    cases = cases.filter((mockCase) => statuses.includes(mockCase.status));
  }

  if (priorities.length > 0) {
    cases = cases.filter((mockCase) => priorities.includes(mockCase.priority));
  }

  if (searchQuery !== undefined && searchQuery !== "") {
    cases = cases.filter((mockCase) =>
      `${mockCase.title} ${mockCase.description}`
        .toLowerCase()
        .includes(searchQuery),
    );
  }

  if (assignees.length > 0) {
    cases = cases.filter(
      (mockCase) =>
        "assignee" in mockCase &&
        typeof mockCase.assignee === "string" &&
        assignees.includes(mockCase.assignee),
    );
  }

  return cases;
};

const buildFilteredCasesResponse = (info: {
  request: Request;
}): ApiCaseListResponse => {
  const cases = filterMockCases(new URL(info.request.url));

  return {
    data: { cases },
    meta: { count: cases.length, page: 1, pageSize: 25 },
    errors: {},
  };
};

export const getCustomCasesHandler = () =>
  getGetCasesMockHandler(buildFilteredCasesResponse);

export const getCustomSavedFiltersHandler = () =>
  getGetFiltersByTableCodeMockHandler((info) => {
    const tableCode = info.params.tableCode as string;

    if (!tableCode.startsWith("cases")) {
      return getGetFiltersByTableCodeResponseMock();
    }

    const casesViewsResponse: ApiResponseSavedFilters = {
      data: [
        {
          id: "cases-view-1",
          filterName: "High priority",
          filterConfig: {
            FilterState: JSON.stringify({
              priorities: {
                value: [{ label: "HIGH", value: "HIGH" }],
                type: "multiselect",
              },
            }),
            toggleState: JSON.stringify({
              visibility: { organisation: false },
            }),
            columnState: JSON.stringify([]),
          },
          createdAt: "2024-01-15T10:30:00Z",
          updatedAt: "2024-01-15T10:30:00Z",
        },
        {
          id: "cases-view-2",
          filterName: "Overdue technical issues",
          filterConfig: {
            FilterState: JSON.stringify({
              categories: {
                value: [
                  {
                    label: "Technical Issues",
                    value: "123e4567-e89b-12d3-a456-426614174012",
                  },
                ],
                type: "multiselect",
              },
              overdueOnly: { value: true, type: "switch" },
            }),
          },
          createdAt: "2024-01-10T14:20:00Z",
          updatedAt: "2024-01-20T09:15:00Z",
        },
        {
          id: "cases-view-3",
          filterName: "Wire instructions",
          filterConfig: {
            FilterState: JSON.stringify({
              searchQuery: { value: "wire", type: "search" },
            }),
          },
          createdAt: "2024-01-05T09:45:00Z",
          updatedAt: "2024-01-25T16:30:00Z",
        },
      ],
      meta: {},
      errors: {},
    };

    return casesViewsResponse;
  });
